/**
 * Wraps a single status-check call with retry-on-network-failure - a single failed request
 * (a brief mobile network blip, a momentary server hiccup) does not mean whatever it was
 * checking on has actually died, so this retries the check itself a few times before giving
 * up, instead of the caller abandoning something that's very likely still running fine.
 *
 * This only wraps ONE fetch attempt with retries - it is not a full polling loop. Each
 * caller keeps its own "is this done yet, should I schedule another check later" logic,
 * since that genuinely differs between a chat task, a build, and an upload; only the
 * resilient-fetch mechanics were actually duplicated three times before this existed.
 *
 * Real, live bug found and fixed: an admin reported "Lost connection while checking task
 * status" for a task that was genuinely still running fine server-side - the old default
 * (3 retries, 2s apart) gives up after roughly 6 seconds total. That is a thin budget
 * against a real, structural fact about how this backend is deployed: deploy-backend.sh
 * runs Cloud Run with min-instances=0 (no idle cost, but it means the service can genuinely
 * be COLD, needing several seconds to boot a fresh instance before it can answer anything
 * at all). 6 seconds is not a realistic worst case for a cold start - widened so a genuine
 * cold start self-heals instead of surfacing as a scary, and misleading, "lost connection".
 */
export async function fetchWithRetry(fetchFn, retriesLeft = 6, delayMs = 3000) {
  try {
    return await fetchFn();
  } catch (err) {
    if (retriesLeft <= 0) throw err;
    await new Promise(resolve => setTimeout(resolve, delayMs));
    return fetchWithRetry(fetchFn, retriesLeft - 1, delayMs);
  }
}
